import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { apiGet } from '../api.js';
import { CollabSurface } from '../editor/CollabSurface.jsx';
import { CollabField } from '../editor/CollabField.jsx';
import { ImageGallery } from '../widgets/ImageGallery.jsx';
import { AttachmentList } from '../widgets/AttachmentList.jsx';
import { ArtworkTab } from '../widgets/ArtworkTab.jsx';
import { ElevenVoiceField } from '../widgets/ElevenVoiceField.jsx';

const TABS = ['background', 'attachments', 'artwork'];

function readInitialTab() {
  if (typeof window === 'undefined') return 'background';
  const h = (window.location.hash || '').replace(/^#/, '');
  return TABS.includes(h) ? h : 'background';
}

export function Character({ session }) {
  const { name } = useParams();
  const navigate = useNavigate();
  // The beat whose Characters tab opened this page (widgets/BeatCharacters.jsx);
  // the breadcrumb goes back there instead of to the TOC.
  const { fromBeat, fromBeatName } = useLocation().state || {};
  const [character, setCharacter] = useState(null);
  const [template, setTemplate] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState(readInitialTab);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [filePickerOpen, setFilePickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, t] = await Promise.all([
          apiGet(`/character?name=${encodeURIComponent(name)}`),
          apiGet('/template'),
        ]);
        if (cancelled) return;
        setCharacter(c.character);
        setTemplate(t.character_template);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [name, refreshKey]);

  useEffect(() => {
    function onHash() {
      const next = readInitialTab();
      setActiveTab(next);
    }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  function selectTab(tab) {
    setActiveTab(tab);
    if (typeof window !== 'undefined') {
      const newHash = tab === 'background' ? '' : `#${tab}`;
      if (window.location.hash !== newHash) {
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${newHash}`);
      }
    }
  }

  function onRefresh() { setRefreshKey((k) => k + 1); }

  if (error) return <div className="app"><div className="error-banner">{error}</div></div>;
  if (!character || !template) {
    return <div className="app"><p style={{ color: 'var(--fg-muted)' }}>Loading character…</p></div>;
  }

  const room = `character:${character._id}`;
  const customFields = (template.fields || []).filter((f) => !f.core);

  return (
    <main className="app">
      <p>
        {fromBeat ? (
          <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/beat/${fromBeat}#characters`); }}>
            ← Beat #{fromBeat}{fromBeatName ? ` — ${fromBeatName}` : ''}
          </a>
        ) : (
          <a href="#" onClick={(e) => { e.preventDefault(); navigate('/'); }}>← Back to TOC</a>
        )}
      </p>
      <h1 style={{ marginTop: 0 }}>{character.name || 'Character'}</h1>

      <div className="tab-nav" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={activeTab === t}
            className={`tab-button${activeTab === t ? ' is-active' : ''}`}
            onClick={() => selectTab(t)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      <CollabSurface room={room} session={session} onPing={onRefresh}>
        <div className="tab-panel" hidden={activeTab !== 'background'}>
          <CollabField label="Name" field="name" />
          <CollabField label="Hollywood actor" field="hollywood_actor" />
          <ElevenVoiceField
            characterId={character._id}
            value={character.eleven_voice || null}
            onChange={onRefresh}
          />

          {customFields.map((f) => (
            <div key={f.name}>
              <CollabField
                label={f.name.replace(/_/g, ' ')}
                field={`fields.${f.name}`}
                multiline
                placeholder={f.description}
              />
              {f.name === 'wardrobe' && (
                <div className="muted" style={{ fontSize: 12, marginTop: -6, marginBottom: 10 }}>
                  {character.wardrobe_image_id
                    ? '👔 Wardrobe plate set — every render of this character copies its clothes (see the Artwork / Attachments tabs).'
                    : 'No wardrobe plate yet — mark one on the Artwork or Attachments tab, or the first costume render from a beat critique becomes it.'}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="tab-panel" hidden={activeTab !== 'attachments'}>
          <p className="tab-intro">
            <strong>Images</strong> are reference images used to create artwork for this character.{' '}
            <strong>Files</strong> are reference material such as PDFs, Word documents, and audio samples.
          </p>
          <div className="tab-actions">
            <button
              type="button"
              className="primary"
              onClick={() => setImagePickerOpen(true)}
            >
              + Add image
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => setFilePickerOpen(true)}
            >
              + Add file
            </button>
          </div>
          <ImageGallery
            images={character.images || []}
            mainImageId={character.main_image_id}
            wardrobeImageId={character.wardrobe_image_id}
            wardrobePath={`/character/${character._id}/wardrobe-image`}
            onChange={onRefresh}
            uploadPath={`/character/${character._id}/image`}
            deletePath={(imageId) => `/character/${character._id}/image/${imageId}`}
            mainPath={`/character/${character._id}/main-image`}
            editPath={(imageId) =>
              `/character/${character._id}/image/${imageId}/regenerate`
            }
            generatePath={`/character/${character._id}/image/generate`}
            characterSourcesPath={`/images/by-owner/characters?exclude_id=${character._id}`}
            beatSourcesPath={`/images/by-owner/beats`}
            setSourcesPath={`/images/by-owner/sets`}
            copyPath={`/character/${character._id}/image/copy`}
            referenceHost={{
              hostType: 'character',
              hostId: character._id,
              hostLabel: character.name,
              hostImages: character.images || [],
              hostArtworks: character.artworks || [],
            }}
            pickerTitle="Add image to character"
            hideAddButton
            pickerOpen={imagePickerOpen}
            onPickerOpenChange={setImagePickerOpen}
          />
          <AttachmentList
            attachments={character.attachments || []}
            onChange={onRefresh}
            uploadPath={`/character/${character._id}/attachment`}
            deletePath={(id) => `/character/${character._id}/attachment/${id}`}
            attachPath={`/character/${character._id}/attachment/attach`}
            pickerTitle="Add file to character"
            fieldPrefix="attachment"
            hideAddButton
            pickerOpen={filePickerOpen}
            onPickerOpenChange={setFilePickerOpen}
          />
        </div>

        <div className="tab-panel" hidden={activeTab !== 'artwork'}>
          <ArtworkTab
            hostType="character"
            hostId={character._id}
            hostLabel={character.name}
            artworks={character.artworks || []}
            hostImages={character.images || []}
            hostArtworks={character.artworks || []}
            mainImageId={character.main_image_id}
            mainPath={`/character/${character._id}/main-image`}
            wardrobeImageId={character.wardrobe_image_id}
            wardrobePath={`/character/${character._id}/wardrobe-image`}
            onChange={onRefresh}
          />
        </div>
      </CollabSurface>
    </main>
  );
}

function tabLabel(tab) {
  switch (tab) {
    case 'background': return 'Background';
    case 'attachments': return 'Attachments';
    case 'artwork': return 'Artwork';
    default: return tab;
  }
}
